import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { TEAMS_TAG_TYPE } from './constants';

export interface TeamUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface Team {
  id: string;
  name: string;
  description: string;
  users: TeamUser[];
  filter_set_folders?: string[];
  editable?: boolean;
  plays_enabled?: boolean;
}

interface TeamsResponse {
  results: Team[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface CreateTeamRequest {
  name: string;
  description: string;
  users: string[];
}

export interface UpdateTeamRequest {
  id: string;
  name?: string;
  description?: string;
  users?: string[];
  plays_enabled?: boolean;
}

export const teamsApi = createApi({
  reducerPath: 'teamsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [TEAMS_TAG_TYPE],
  endpoints: (builder) => ({
    getAllTeams: builder.query<TeamsResponse, void>({
      query: () => ({
        url: 'v1/accounts/teams/',
        method: 'GET',
      }),
      providesTags: [{ type: TEAMS_TAG_TYPE, id: 'LIST' }],
    }),

    getTeamById: builder.query<Team, string>({
      query: (id) => ({
        url: `v1/accounts/teams/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TEAMS_TAG_TYPE, id }],
    }),

    createTeam: builder.mutation<Team, CreateTeamRequest>({
      query: (data) => ({
        url: 'v1/accounts/teams/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TEAMS_TAG_TYPE, id: 'LIST' }],
    }),

    updateTeam: builder.mutation<Team, UpdateTeamRequest>({
      query: ({ id, ...data }) => ({
        url: `v1/accounts/teams/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TEAMS_TAG_TYPE, id: 'LIST' },
        { type: TEAMS_TAG_TYPE, id },
      ],
    }),

    deleteTeam: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/accounts/teams/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TEAMS_TAG_TYPE, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAllTeamsQuery,
  useGetTeamByIdQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
} = teamsApi;
